<!--
  Admin shell.

  Thin wrapper over `AppShell` from `@lucent/ui`, which owns the chrome both
  SPAs share: sticky header with brand lockup + nav + theme toggle, centred
  content shell, footer bar. What is left here is only what is specific to the
  admin — the realm chip text, the six nav sections and their active-state
  rules, the signed-in operator chip, and the footer's content.

  The theme state machine moved with the chrome. `AppShell` installs
  `useTheme()` internally, so this file no longer touches `localStorage`,
  `prefers-color-scheme`, or the `data-theme` attribute.
-->

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { RouterLink, RouterView, useRoute } from "vue-router";

import { AppShell } from "@lucent/ui";

import { currentAdmin, loadCurrentAdmin, logout } from "./auth.ts";

const route = useRoute();

const userLabel = computed(() =>
  currentAdmin.value
    ? currentAdmin.value.preferred_username ??
      currentAdmin.value.email ??
      currentAdmin.value.name ??
      currentAdmin.value.sub
    : null,
);

const onSignOut = (): void => {
  void logout();
};

// Nav active-state computeds. Each lights up the section button for
// every route inside that section so child detail routes don't
// orphan the highlight.
const overviewActive = computed(() => route.name === "overview");
const clientsActive = computed(() =>
  ["clients", "client-detail", "clients-data-plane"].includes(
    String(route.name ?? ""),
  ),
);
const insightsActive = computed(() =>
  ["insights", "insight-view", "insight-create"].includes(String(route.name ?? "")),
);
const usersActive = computed(() => route.name === "users");
// The four log streams are one nav section; the tab strip inside
// `Logs.vue` picks which one. Listed here rather than matched on the
// `/logs` path prefix so the nav keeps working the same way every other
// section does — off route names, not URLs.
//
// Every stream in `Logs.vue`'s `STREAMS` must appear here: a missing one
// drops the nav highlight while its own tab still shows as active, which
// reads as having navigated out of the section. `logs-user-actions` was
// missing from this list from the day that stream landed.
const logsActive = computed(() =>
  [
    "logs-queries",
    "logs-embed-tokens",
    "logs-admin-actions",
    "logs-user-actions",
  ].includes(String(route.name ?? "")),
);
const userSyncActive = computed(() => route.name === "sync-users");

// The login screen is a full-bleed bespoke layout — suppress the
// global header / footer / centred shell so the sign-in card claims
// the viewport, same as the dashboard's auth route. This stays here
// rather than in AppShell: the shell reads no route state, and the app
// is the only party that knows which route is the bespoke one.
const isAuthRoute = computed(() => route.name === "login");

onMounted(async () => {
  // Make sure the auth state is hydrated before the header renders
  // its signed-in chrome (otherwise the user chip flashes empty on
  // first paint). The router guard also calls this; it's idempotent.
  await loadCurrentAdmin();
});
</script>

<template>
  <AppShell
    :chromeless="isAuthRoute"
    main="centred"
    home-to="/"
    lockup-label="LucentLake Admin home"
  >
    <template #brand-sub>Admin · Operator</template>

    <template #nav>
      <RouterLink to="/" :class="{ 'is-active': overviewActive }">Overview</RouterLink>
      <RouterLink to="/clients" :class="{ 'is-active': clientsActive }">Clients</RouterLink>
      <RouterLink to="/users" :class="{ 'is-active': usersActive }">Users</RouterLink>
      <RouterLink to="/insights" :class="{ 'is-active': insightsActive }">Insights</RouterLink>
      <RouterLink to="/logs" :class="{ 'is-active': logsActive }">Logs</RouterLink>
      <RouterLink to="/sync/users" :class="{ 'is-active': userSyncActive }">User Sync</RouterLink>
    </template>

    <!--
      Signed-in chrome: appears once `currentAdmin` is populated. The label
      prefers `preferred_username` so the IdP's human-friendly handle wins
      over the opaque `sub`.
    -->
    <template #header-actions>
      <span v-if="userLabel" class="lucent-user-chip" :title="userLabel">
        {{ userLabel }}
      </span>
      <button
        v-if="userLabel"
        type="button"
        class="lucent-btn lucent-btn--ghost"
        @click="onSignOut"
      >
        Sign out
      </button>
    </template>

    <RouterView />

    <template #footer>
      <span class="lucent-footer-status">
        <span class="dot"></span>Admin · cross-tenant
      </span>
      <span class="lucent-footer-sep"></span>
      <span>© 2026 LucentLake · Savage Bull</span>
      <nav class="lucent-footer-links" aria-label="Footer">
        <a href="/healthz" target="_blank" rel="noopener">Health</a>
      </nav>
    </template>
  </AppShell>
</template>
